<script setup lang="ts">
// Métadonnées de la page des articles
false && /*#__PURE__*/ defineI18nRoute({
  paths: {
    en: '/articles',
    fr: '/articles',
  },
})

definePageMeta({
  name: 'ArticlesPage',
})

const route = useRoute()
const config = useRuntimeConfig()
const baseStore = useBaseStore()
const userStore = useUserStore()

useHead({
  title: 'Articles et conseils - Glouton',
  meta: [
    { name: 'description', content: 'Découvrez nos articles et conseils pour optimiser vos courses et votre cuisine. Trucs et astuces pour économiser.' },
    { property: 'og:title', content: 'Articles et conseils - Glouton' },
    { property: 'og:description', content: 'Découvrez nos articles et conseils pour optimiser vos courses et votre cuisine. Trucs et astuces pour économiser.' },
    { property: 'og:url', content: computed(() => `${config.public.domain}${route.path}`) },
    { property: 'og:image', content: 'https://glouton.b-cdn.net/site/images/social.jpg' },
  ],
})

// SSR + hydration with reactive page parameter
const { data: articlesData, pending } = await useAsyncData(
  'articles',
  () => $fetch(`${config.public.apiUrl}/blogs/fetch-articles`, {
    query: {
      page: Number.parseInt(route.query.page as string) || 1,
      category: false,
    },
  }),
  {
    default: () => ({
      articles: {
        data: [],
        total: 0,
        last_page: 0,
        current_page: 1,
      },
    }),
    watch: [() => route.query.page],
  },
)

// Reactive data from API response
const articles = computed(() => (articlesData.value as any)?.articles?.data || [])
const totalRecords = computed(() => (articlesData.value as any)?.articles?.total || 0)
const totalPages = computed(() => (articlesData.value as any)?.articles?.last_page || 1)
const currentPage = computed(() => (articlesData.value as any)?.articles?.current_page || 1)

// Pagination function for the 'to' prop
function to(page: number) {
  return {
    query: { page: page.toString() },
  }
}

// Initialize
onMounted(() => {
  // Scroll to top
  document.querySelector('body')?.scrollIntoView({ behavior: 'smooth' })
})
</script>

<template>
  <div id="ArticlesPage">
    <TopNav
      title="Articles"
      description="Pour en apprendre plus sur Glouton, la cuisine et la nutrition."
      class="mx-auto max-w-7xl w-full"
    />

    <UContainer id="articles">
      <div v-if="pending" class="flex justify-center items-center py-12">
        <UIcon name="i-mdi-loading" class="animate-spin text-venice text-2xl" />
      </div>

      <div v-else class="py-6">
        <!-- Articles Grid -->
        <div v-if="articles.length > 0" class="flex flex-col space-y-2 sm:space-y-0 sm:flex-row sm:space-x-3">
          <div class="grid grid-cols-1 gap-6 pb-4 sm:grid-cols-2 lg:grid-cols-3">
            <template v-for="(article, index) in articles" :key="article.id">
              <div class="flex flex-col h-full">
                <ArticleCard :article="article" />
              </div>
              <div
                v-if="[2, 6, 10].includes(Number(index) + 1) && baseStore.showAds && baseStore?.platform === 'web' && (!userStore?.user || userStore.user.extra != 1)"
                class="flex flex-col"
              >
                <DisplayAd
                  dfp-ad-unit-path="glouton/articles"
                  :sizes="[[300, 250]]"
                  :size-mapping="[[[0, 0], [[300, 250]]]]"
                  custom-class="h-full"
                />
              </div>
            </template>
          </div>
        </div>

        <!-- Pagination -->
        <DisplayAd
          v-if="baseStore.showAds && baseStore?.platform === 'web' && (!userStore?.user || userStore.user.extra != 1)"
          dfp-ad-unit-path="glouton/articles"
          :sizes="[[320, 50], [728, 90]]"
          :size-mapping="[[[0, 0], [[320, 50]]], [[1024, 0], [[728, 90]]]]"
          custom-class="mb-6"
        />
        <Paginator
          v-if="articles.length > 0"
          class="flex justify-center mt-4 mb-4"
          :total-pages="totalPages"
          :total="totalRecords"
          :per-page="12"
          :current-page="currentPage"
          @pagechanged="(newPage) => currentPage = newPage"
        />

        <!-- No articles found -->
        <div v-if="articles.length === 0 && !pending" class="text-center py-12">
          <UIcon name="i-mdi-file-document-outline" class="text-4xl text-poivre-400 mb-4" />
          <h2 class="text-xl font-semibold text-poivre-800 mb-2">
            Aucun article trouvé
          </h2>
          <p class="text-poivre-600">
            Aucun article n'est disponible pour le moment.
          </p>
        </div>
      </div>
    </UContainer>
  </div>
</template>

<style scoped>
/* Custom styles if needed */
</style>
